/**
 * Sites & Sandboxes: shared types and pure helpers (query building, row
 * predicates, the "does this backend have the API yet" probe).
 *
 * Contract: GET /api/license/admin/sites-sandboxes and .../:id in
 * wpcursor-backend (src/routes/sitesAdmin.js). The payload NEVER contains
 * credentials, so nothing here needs to (or could) display one.
 */

import { backendApi, ApiError } from './api';

export type SiteType = 'site' | 'sandbox';
export type SiteState = 'active' | 'deactivated' | 'archived';

export interface SandboxInfo {
  trialId: string | null;
  status: string | null;
  live: boolean;
  persisted: boolean;
  linkName: string | null;
  linkHost: string | null;
  url: string | null;
  expiresAt: string | null;
  pool: boolean;
}

export interface SiteRow {
  id: string;
  type: SiteType;
  state: SiteState;
  url: string | null;
  openUrl: string | null;
  title: string | null;
  account: { id: string; email: string; name: string | null; accountType: string | null; status: string | null };
  plan: { id: string; name: string };
  createdAt: string | null;
  lastSeenAt: string | null;
  pluginVersion: string | null;
  wpVersion: string | null;
  releaseChannel: string | null;
  deactivatedReason: string | null;
  deactivatedAt: string | null;
  creditsUsed: number;
  sandbox: SandboxInfo | null;
}

/** One metric set, for real sites only or sandboxes only. */
export interface TypeSummary {
  total: number; active: number; deactivated: number; archived: number;
  createdLast7Days: number; activeLast7Days: number; noPluginConnection: number;
}

export interface SitesSummary {
  total: number; sites: number; sandboxes: number; active: number;
  liveSandboxes: number; deactivated: number; archived: number;
  createdLast7Days: number; activeLast7Days: number; noPluginConnection: number;
  /** Every metric split by type. Absent on a backend that predates the split. */
  byType?: { site: TypeSummary; sandbox: TypeSummary };
}

export interface ActivityRow { type: string; credits: number; description: string | null; at: string | null }

export interface ActivityPage { activity: ActivityRow[]; total: number; limit: number; offset: number }

export interface SitesListResponse {
  sites: SiteRow[]; total: number; limit: number; offset: number; summary: SitesSummary;
}

export interface SiteDetails extends SiteRow {
  handoffMode: boolean;
  account: SiteRow['account'] & { createdAt: string | null; creditsBalance: number; creditsUsedTotal: number };
  connectors: {
    firstParty: Record<string, boolean>;
    composio: { toolkit: string; scope: 'site' | 'account' }[];
  };
  recentActivity: ActivityRow[];
  /** null when the caller lacks accounts:read (chat titles are customer content). */
  conversations: { id: string; title: string; messageCount: number; status: string; createdAt: string | null; lastMessageAt: string | null }[] | null;
  conversationCount: number | null;
  conversationsRestricted: boolean;
}

export const SITES_PATH = '/license/admin/sites-sandboxes';
export const PAGE_SIZE = 25;
export const ACTIVITY_PAGE_SIZE = 10;

/** One page of a site's credit history (newest first). */
export function activityPath(id: string, page: number, pageSize = ACTIVITY_PAGE_SIZE): string {
  return `${SITES_PATH}/${encodeURIComponent(id)}/activity?limit=${pageSize}&offset=${Math.max(0, page) * pageSize}`;
}

/**
 * The small line under a summary number: "20 sites · 6 sandboxes", or who the
 * whole number belongs to when one side is empty ("Sites only").
 */
export function tileSplit(sites: number, sandboxes: number): string | undefined {
  if (sites === 0 && sandboxes === 0) return undefined;
  if (sandboxes === 0) return 'Sites only';
  if (sites === 0) return 'Sandboxes only';
  return typeSplit(sites, sandboxes);
}

/** "127 sites · 198 sandboxes" */
export function typeSplit(sites: number, sandboxes: number): string {
  return `${sites.toLocaleString()} ${sites === 1 ? 'site' : 'sites'} · ${sandboxes.toLocaleString()} ${sandboxes === 1 ? 'sandbox' : 'sandboxes'}`;
}

/** A sandbox that still expires, so Extend and Keep forever mean something. */
export function canExtendOrKeep(sandbox: SandboxInfo | null): boolean {
  return !!sandbox && !!sandbox.trialId && sandbox.live && !sandbox.persisted;
}

export type SortKey = 'newest' | 'oldest' | 'seen_recent' | 'seen_stale';
const SORTS: Record<SortKey, { sort: string; dir: string }> = {
  newest: { sort: 'created', dir: 'desc' },
  oldest: { sort: 'created', dir: 'asc' },
  seen_recent: { sort: 'last_seen', dir: 'desc' },
  seen_stale: { sort: 'last_seen', dir: 'asc' },
};

export interface ListFilters {
  search: string; type: '' | SiteType; state: '' | SiteState; plan: string; sort: SortKey; page: number;
}

export const EMPTY_FILTERS: ListFilters = { search: '', type: '', state: '', plan: '', sort: 'newest', page: 0 };

/** The query string for a list request; empty filters are left out entirely. */
export function buildListQuery(f: ListFilters, pageSize = PAGE_SIZE): string {
  const p = new URLSearchParams();
  const search = f.search.trim();
  if (search) p.set('search', search);
  if (f.type) p.set('type', f.type);
  if (f.state) p.set('state', f.state);
  if (f.plan) p.set('plan', f.plan);
  const s = SORTS[f.sort] ?? SORTS.newest;
  p.set('sort', s.sort); p.set('dir', s.dir);
  p.set('limit', String(pageSize));
  p.set('offset', String(Math.max(0, f.page) * pageSize));
  return p.toString();
}

/** A backend that predates the Sites API answers 404 for the route. */
export function isEndpointMissing(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/** A sandbox whose container is still up, i.e. there is something to destroy. */
export function isDestroyable(row: Pick<SiteRow, 'type' | 'sandbox'>): boolean {
  return row.type === 'sandbox' && !!row.sandbox?.trialId && row.sandbox.live === true;
}

let probe: Promise<boolean> | null = null;
/**
 * Does the connected backend serve the Sites API? Asked once per page load and
 * cached. Only a 404 says "no": a 403/500/network blip keeps the entry visible
 * so the page itself can report what went wrong. Used by the sidebar, which
 * hides the entry so nobody navigates to a section the backend cannot serve.
 */
export function sitesApiAvailable(): Promise<boolean> {
  if (!probe) {
    probe = backendApi.get(`${SITES_PATH}?limit=1`).then(() => true, (err) => !isEndpointMissing(err));
  }
  return probe;
}

/** Test seam: forget the cached probe (e.g. after switching Live/Dev). */
export function resetSitesApiProbe(): void { probe = null; }
